import { defineStore } from 'pinia'
import { setupService } from '@/services/setup.js'
import { ANALYTICS_EVENTS } from '@/constants/analyticsEvents.ts'
import analytics from '@/services/analytics.ts'

export const useDartSetupStore = defineStore('dartSetup', {
    state: () => ({
        formData: {
            name: '',
            dart_id: null,
            tip_id: null,
            barrel_id: null,
            shaft_id: null,
            flight_id: null,
            dart_comment: '',
            tip_comment: '',
            barrel_comment: '',
            shaft_comment: '',
            flight_comment: '',
        },
        selectedProducts: {
            dart: null,
            tip: null,
            barrel: null,
            shaft: null,
            flight: null,
        },
        isDefaultSetup: false,
        isReturningFromProductSelection: false,
        // Where to go when leaving the setup create/edit/detail flow entirely.
        // Set once by whoever navigates INTO the flow, read once by whoever
        // navigates OUT of it (back button or successful submit) - this way it
        // survives any number of hops through setup-select-product without
        // needing to be re-threaded through every route's query params.
        // Shape: { type: 'route', name, params } | { type: 'invite' } | null
        entryPoint: null,
    }),

    persist: {
        key: 'dartshopper-draft-setup',
        storage: localStorage,
        paths: ['formData', 'isDefaultSetup'],
    },

    getters: {
        hasStartedCustomizing(state) {
            return (
                !!state.formData.dart_id ||
                !!state.formData.tip_id ||
                !!state.formData.barrel_id ||
                !!state.formData.shaft_id ||
                !!state.formData.flight_id
            )
        },
        isCustomizeComplete(state) {
            return (
                !!state.formData.dart_id ||
                !!state.formData.tip_id ||
                !!state.formData.barrel_id ||
                !!state.formData.shaft_id ||
                !!state.formData.flight_id
            )
        },
        isFormValid() {
            return this.isCustomizeComplete
        },
    },

    actions: {
        setFormData(data) {
            this.formData = { ...this.formData, ...data }
        },

        async setComponent(component, productId) {
            if (component === 'dart') {
                this.formData.dart_id = productId
                this.formData.tip_id = null
                this.formData.barrel_id = null
                this.formData.shaft_id = null
                this.formData.flight_id = null
                this.selectedProducts.tip = null
                this.selectedProducts.barrel = null
                this.selectedProducts.shaft = null
                this.selectedProducts.flight = null
            } else {
                this.formData.dart_id = null
                this.selectedProducts.dart = null
                this.formData[`${component}_id`] = productId
            }

            // The route-query path back from ProductSelectionView, which is how a product is
            // actually picked in the create/edit flow - setProduct() below covers the inline
            // ProductSearch dropdown.
            analytics.trackEvent(ANALYTICS_EVENTS.PRODUCT_SELECTED, {
                component,
                product_id: productId,
                replaces_components: component === 'dart',
            })

            try {
                const response = await setupService.searchProducts('', component)
                const product = response.data?.find((p) => p.id === productId)
                if (product) {
                    this.selectedProducts[component] = product
                }
            } catch (error) {
                console.error(`Failed to fetch ${component} product:`, error)
            }
        },

        setProduct(component, product) {
            if (component === 'dart') {
                this.formData.dart_id = product.id
                this.formData.tip_id = null
                this.formData.barrel_id = null
                this.formData.shaft_id = null
                this.formData.flight_id = null
                this.selectedProducts.tip = null
                this.selectedProducts.barrel = null
                this.selectedProducts.shaft = null
                this.selectedProducts.flight = null
            } else {
                this.formData.dart_id = null
                this.selectedProducts.dart = null
                this.formData[`${component}_id`] = product.id
            }
            this.selectedProducts[component] = product

            analytics.trackEvent(ANALYTICS_EVENTS.PRODUCT_SELECTED, {
                component,
                product_id: product.id,
                // A whole-dart pick clears the individual components, so the two are worth
                // telling apart when looking at what people actually configure.
                replaces_components: component === 'dart',
            })
        },

        removeComponent(component) {
            if (component === 'dart') {
                this.formData.dart_id = null
                this.formData.dart_comment = ''
                this.selectedProducts.dart = null
            } else {
                this.formData[`${component}_id`] = null
                this.formData[`${component}_comment`] = ''
                this.selectedProducts[component] = null
            }
        },

        setName(name) {
            this.formData.name = name
        },

        setComponentComment(component, comment) {
            this.formData[`${component}_comment`] = comment
        },

        setIsDefaultSetup(value) {
            this.isDefaultSetup = value
        },

        clearDraft() {
            this.formData = {
                name: '',
                dart_id: null,
                tip_id: null,
                barrel_id: null,
                shaft_id: null,
                flight_id: null,
                dart_comment: '',
                tip_comment: '',
                barrel_comment: '',
                shaft_comment: '',
                flight_comment: '',
            }
            this.selectedProducts = {
                dart: null,
                tip: null,
                barrel: null,
                shaft: null,
                flight: null,
            }
            this.isDefaultSetup = false
        },

        async initializeFromSetup(setup) {
            if (setup) {
                this.formData = {
                    name: setup.name || '',
                    dart_id: setup.dart?.id || setup.dart_id || null,
                    tip_id: setup.tip?.id || setup.tip_id || null,
                    barrel_id: setup.barrel?.id || setup.barrel_id || null,
                    shaft_id: setup.shaft?.id || setup.shaft_id || null,
                    flight_id: setup.flight?.id || setup.flight_id || null,
                    dart_comment: setup.dart_comment || '',
                    tip_comment: setup.tip_comment || '',
                    barrel_comment: setup.barrel_comment || '',
                    shaft_comment: setup.shaft_comment || '',
                    flight_comment: setup.flight_comment || '',
                }
                if (setup.dart) this.selectedProducts.dart = setup.dart
                if (setup.tip) this.selectedProducts.tip = setup.tip
                if (setup.barrel) this.selectedProducts.barrel = setup.barrel
                if (setup.shaft) this.selectedProducts.shaft = setup.shaft
                if (setup.flight) this.selectedProducts.flight = setup.flight
            }
        },

        setReturningFromProductSelection(value) {
            this.isReturningFromProductSelection = value
        },

        setEntryPoint(entryPoint) {
            this.entryPoint = entryPoint
        },

        clearEntryPoint() {
            this.entryPoint = null
        },
    },
})
